import React, { useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaCheck } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./Register.css";
import Swal from 'sweetalert2';
import BaseUrl from '../../ApiServices/BaseUrl';

const Register = ({ onSuccess }) => {
  const [userType, setUserType] = useState("jobSeeker");
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");

  const userOptions = [
    {
      value: "jobSeeker",
      label: "Job Seeker",
      sub: "I am looking for job",
    },
    {
      value: "employer",
      label: "Employer",
      sub: "I Want to hire",
    },
    {
      value: "agency",
      label: "Agency",
      sub: "Recruitment Agency",
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value.trim())) {
        setEmailError("Please enter a valid email address");
      } else {
        setEmailError("");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      Swal.fire({
        icon: 'warning',
        title: 'Oops!',
        text: 'Passwords do not match',
        confirmButtonColor: '#f27474'
      });
      return;
    }

    const roleMap = {
      jobSeeker: "job seeker",
      employer: "employer",
      agency: "agency",
    };

    const payload = {
      first_name: formData.first_name,
      last_name: formData.last_name || formData.first_name,
      email: formData.email,
      password: formData.password,
      role: roleMap[userType],
    };

    try {
      setLoading(true);

      const response = await fetch(`${BaseUrl}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        Swal.fire({
          icon: 'error',
          title: 'Registration Failed',
          text: data.error || 'Unknown error',
          confirmButtonColor: '#d33'
        });
        return;
      }

      // Auto login after successful registration
      const loginRes = await fetch(`${BaseUrl}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email, password: formData.password }),
      });

      const loginData = await loginRes.json();

      if (!loginRes.ok) {
        throw new Error(loginData.message || "Auto login failed");
      }

      // Save user to localStorage
      localStorage.setItem("user", JSON.stringify(loginData.user));

      Swal.fire({
        icon: 'success',
        title: 'Registration & Login Successful!',
        text: `Welcome, ${loginData.user.first_name || 'User'}!`,
        confirmButtonColor: '#28a745'
      }).then(() => {
        // Redirect based on role
        if (loginData.user.role === 'employer') {
          navigate("/employernavbar");
        } else {
          navigate("/");
        }
        window.location.reload(); // Optional: Refresh to update app state
      });

      // Reset form
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        confirmPassword: "",
      });

      if (onSuccess) onSuccess(); // Optional: close modal if provided

    } catch (error) {
      console.error("Error registering or logging in:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Please try again later.',
        confirmButtonColor: '#d33'
      });
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="register-popup-container">
      {/* <div className="rounded px-3 py-2 mb-3 text-white bg-primary small">
        Register on GulfWorker.net to quickly connect with employers, domestic helpers,
        and manpower recruitment agencies based on your requirements.
      </div> */}

      <div className="bg-primary text-white rounded d-flex justify-content-around align-items-center text-center mb-4 py-3 flex-wrap">
        <div
          className="text-white mx-1 px-2 d-flex flex-column align-items-center"
          style={{ fontSize: "13px", minWidth: "100px" }}
        >
          <div className="d-flex align-items-center">
            <div className="custom-radio-icon me-2 selected">
              <FaCheck size={10} color="#fff" />
            </div>
            <span className="fw-semibold">Job Seeker</span>
          </div>
          <div className="text-white-50" style={{ fontSize: "11px" }}>
            Looking for a job
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-2">
          <label className="form-label small mb-1">First Name</label>
          <input
            type="text"
            name="first_name"
            className="form-control rounded-input"
            value={formData.first_name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="mb-2">
          <label className="form-label small mb-1">Last Name</label>
          <input
            type="text"
            name="last_name"
            className="form-control rounded-input"
            value={formData.last_name}
            onChange={handleChange}
          />
        </div>

        <div className="mb-2">
          <label className="form-label small mb-1">Email</label>
          <input
            type="email"
            name="email"
            className={`form-control rounded-input ${emailError ? 'is-invalid' : ''}`}
            value={formData.email}
            onChange={handleChange}
            required
          />
          {emailError && (
            <div className="invalid-feedback d-block">{emailError}</div>
          )}
        </div>

        <div className="mb-2 position-relative">
          <label className="form-label small mb-1">Password</label>
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            className="form-control rounded-input pe-5"
            placeholder="Minimum 8 characters"
            value={formData.password}
            onChange={handleChange}
            required
          />
          <i
            className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"} position-absolute`}
            style={{
              top: "38px",
              right: "15px",
              cursor: "pointer",
              color: "#6c757d"
            }}
            onClick={() => setShowPassword((prev) => !prev)}
          ></i>
        </div>

        <div className="mb-3">
          <label className="form-label small mb-1">Confirm Password</label>
          <input
            type="password"
            name="confirmPassword"
            className="form-control rounded-input"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
          />
        </div>

        <button
          type="submit"
          className="btn btn-primary w-100 d-flex justify-content-center align-items-center rounded-pill py-2"
          disabled={loading}
        >
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <span style={{ fontSize: "17px" }}>
              {loading ? "Registering..." : "Create an account"}
            </span>
            {!loading && (
              <span style={{ fontSize: "28px", lineHeight: "1", marginBottom: "4px" }}>
                &#8250;
              </span>
            )}
          </div>
        </button>
      </form>

      <div className="mt-3 text-center">
        <p className="small">Already have an account?
          <button
            className="btn btn-link p-0 ms-1 text-primary"
            onClick={() => {
              if (onSuccess) onSuccess();
              window.location.href = '/login';
            }}
          >
            Login here
          </button>
        </p>
      </div>
    </div>
  );
};

export default Register;